// ═══════════════════════════════════════════════════════════════════
// Abel Alignment Engine — data layer
// The scoring system stays completely hidden. Users only see observations.
// ═══════════════════════════════════════════════════════════════════

export const ABEL_FREQUENCY = {
  builder_energy:      0.95,
  ambitious:           0.90,
  emotionally_intense: 0.90,
  future_oriented:     0.90,
  curious:             0.85,
  aesthetic_driven:    0.85,
  playful:             0.85,
  underground_scene:   0.85,
  introspective:       0.80,
  romantic:            0.80,
  empathetic:          0.75,
  chaotic:             0.75,
  guarded:             0.70,
  tender:              0.65,
};

// ── Demographic openers (styled, not clinical) ────────────────────────

export const DEMO_QUESTIONS = [
  {
    id: 'entity',
    question: 'What kind of entity are you?',
    answers: [
      { text: 'girl',         key: 'girl' },
      { text: 'guy',          key: 'guy' },
      { text: 'they/them',    key: 'theythem' },
      { text: 'cryptid',      key: 'cryptid' },
      { text: 'classified',   key: 'classified' },
    ],
  },
  {
    id: 'arc',
    question: 'Current life arc?',
    answers: [
      { text: '18–20',          key: '18-20' },
      { text: '21–24',          key: '21-24' },
      { text: '25–29',          key: '25-29' },
      { text: '30+',            key: '30+' },
      { text: 'spiritually 47', key: 'spiritually47' },
    ],
  },
];

// ── Questions — 10 tight, human, cinematic ───────────────────────────

export const QUESTIONS = [
  {
    id: 'night',
    question: 'What kind of night changes your life a little?',
    answers: [
      {
        text: "Warehouse show — a crowd that doesn't care who you are",
        traits: { underground_scene: 0.9, chaotic: 0.7, aesthetic_driven: 0.6 },
      },
      {
        text: 'Building something at 2am that might be nothing or everything',
        traits: { builder_energy: 0.95, ambitious: 0.8, introspective: 0.6 },
      },
      {
        text: 'A parking lot conversation that went deeper than expected',
        traits: { emotionally_intense: 0.9, romantic: 0.8, introspective: 0.8 },
      },
      {
        text: 'A quiet night that somehow turned into exactly what you needed',
        traits: { tender: 0.7, introspective: 0.7, aesthetic_driven: 0.5 },
      },
      {
        text: 'Disappearing into the city alone — better that way',
        traits: { chaotic: 0.8, introspective: 0.7, guarded: 0.6 },
      },
    ],
  },
  {
    id: 'disappear',
    question: 'You disappear for 8 hours. Why?',
    answers: [
      {
        text: 'Working on something that finally has momentum',
        traits: { builder_energy: 0.9, ambitious: 0.85, future_oriented: 0.8 },
      },
      {
        text: 'Social battery hit zero — needed total quiet',
        traits: { guarded: 0.8, introspective: 0.6, empathetic: 0.5 },
      },
      {
        text: 'Something hit hard and needed space to process it',
        traits: { emotionally_intense: 0.9, introspective: 0.9, tender: 0.6 },
      },
      {
        text: 'Physical reset — moving, training, clearing the head',
        traits: { ambitious: 0.7, future_oriented: 0.6 },
      },
      {
        text: 'The world just receded for a while. It was fine.',
        traits: { chaotic: 0.6, introspective: 0.7, guarded: 0.5 },
      },
    ],
  },
  {
    id: 'killer',
    question: 'What kills it fastest?',
    answers: [
      {
        text: 'Someone comfortable going nowhere',
        traits: { ambitious: 0.9, future_oriented: 0.85 },
      },
      {
        text: 'Fake energy — performing for the room instead of being in it',
        traits: { playful: 0.85, curious: 0.8, empathetic: 0.7 },
      },
      {
        text: 'Needing constant reassurance',
        traits: { guarded: 0.75, chaotic: 0.5 },
      },
      {
        text: 'Using ambition as an excuse for bad behavior',
        traits: { empathetic: 0.9, romantic: 0.7, tender: 0.65 },
      },
      {
        text: 'No capacity for humor — everything is a serious situation',
        traits: { playful: 0.9, curious: 0.7 },
      },
    ],
  },
  {
    id: 'intimate',
    question: 'What feels unexpectedly intimate?',
    answers: [
      {
        text: 'Someone remembering small details without being asked',
        traits: { tender: 0.9, romantic: 0.8, emotionally_intense: 0.7 },
      },
      {
        text: "Silence that doesn't need filling",
        traits: { introspective: 0.85, aesthetic_driven: 0.6, guarded: 0.4 },
      },
      {
        text: 'Sending songs back and forth without explanation',
        traits: { aesthetic_driven: 0.8, romantic: 0.75, playful: 0.7 },
      },
      {
        text: "Being included in someone's actual routine",
        traits: { tender: 0.85, romantic: 0.8, emotionally_intense: 0.6 },
      },
      {
        text: 'Building or making something together',
        traits: { builder_energy: 0.75, romantic: 0.7, curious: 0.65 },
      },
    ],
  },
  {
    id: 'process',
    question: 'Something hits you hard. What happens first?',
    answers: [
      {
        text: 'Analyze it until the pattern makes sense',
        traits: { introspective: 0.9, emotionally_intense: 0.8, curious: 0.7 },
      },
      {
        text: 'Turn it into something — project, song, plan',
        traits: { builder_energy: 0.85, emotionally_intense: 0.85, aesthetic_driven: 0.65 },
      },
      {
        text: 'Disappear, then reappear different',
        traits: { chaotic: 0.7, introspective: 0.8, future_oriented: 0.75 },
      },
      {
        text: 'Find one specific person and talk for hours',
        traits: { romantic: 0.75, tender: 0.7, empathetic: 0.65 },
      },
      {
        text: 'Let it sit. Come back to it later.',
        traits: { introspective: 0.8, guarded: 0.7 },
      },
    ],
  },
  {
    id: 'atmosphere',
    question: 'What hits different when the vibe is exactly right?',
    answers: [
      {
        text: 'Music + lighting + the exact atmosphere — nothing else needs to happen',
        traits: { aesthetic_driven: 0.95, underground_scene: 0.8, emotionally_intense: 0.65 },
      },
      {
        text: 'A conversation going somewhere nobody planned',
        traits: { emotionally_intense: 0.85, introspective: 0.8, romantic: 0.75 },
      },
      {
        text: 'The feeling that something is being made or built',
        traits: { builder_energy: 0.8, ambitious: 0.7, future_oriented: 0.65 },
      },
      {
        text: 'Chemistry in a room — that invisible thing',
        traits: { playful: 0.8, curious: 0.75, empathetic: 0.7 },
      },
      {
        text: 'One specific person at exactly the right moment',
        traits: { romantic: 0.9, emotionally_intense: 0.85, tender: 0.7 },
      },
    ],
  },
  {
    id: 'home',
    question: 'Where do you actually feel at home?',
    answers: [
      {
        text: 'Somewhere loud and chaotic — show, venue, underground anything',
        traits: { underground_scene: 0.95, chaotic: 0.8, aesthetic_driven: 0.6 },
      },
      {
        text: "Late night, right people, doesn't matter where",
        traits: { romantic: 0.8, emotionally_intense: 0.75, playful: 0.6 },
      },
      {
        text: 'Flow state — working, everything else disappears',
        traits: { builder_energy: 0.9, ambitious: 0.85, introspective: 0.6 },
      },
      {
        text: 'Nowhere consistent — I move between worlds',
        traits: { chaotic: 0.75, curious: 0.8, guarded: 0.5 },
      },
      {
        text: 'Somewhere quiet that fits just right',
        traits: { aesthetic_driven: 0.75, introspective: 0.7, tender: 0.6 },
      },
    ],
  },
  {
    id: 'humor',
    question: 'Your humor is mostly:',
    answers: [
      {
        text: 'Absurdist and layered — references nobody asked for',
        traits: { playful: 0.9, curious: 0.85, introspective: 0.65 },
      },
      {
        text: "Self-aware — you're the bit",
        traits: { playful: 0.95, guarded: 0.45, emotionally_intense: 0.6 },
      },
      {
        text: 'Observational — the world is the joke',
        traits: { introspective: 0.8, curious: 0.8, empathetic: 0.65 },
      },
      {
        text: "Dark but careful — not everyone gets it",
        traits: { playful: 0.75, guarded: 0.65, emotionally_intense: 0.7 },
      },
      {
        text: 'Situational — lives in the moment, hard to reproduce',
        traits: { playful: 0.7, chaotic: 0.65, curious: 0.6 },
      },
    ],
  },
  {
    id: 'understood',
    question: 'What would you want someone to actually understand about you?',
    answers: [
      {
        text: "That I'm building toward something, always",
        traits: { ambitious: 0.9, builder_energy: 0.85, future_oriented: 0.9 },
      },
      {
        text: 'That I go deeper than I show',
        traits: { emotionally_intense: 0.9, guarded: 0.8, introspective: 0.85 },
      },
      {
        text: 'That I take loyalty seriously',
        traits: { romantic: 0.9, emotionally_intense: 0.8, tender: 0.7 },
      },
      {
        text: "That I'm funnier than I am serious",
        traits: { playful: 0.9, guarded: 0.45, empathetic: 0.6 },
      },
      {
        text: "That I contain contradictions and that's intentional",
        traits: { chaotic: 0.7, introspective: 0.8, emotionally_intense: 0.75 },
      },
    ],
  },
  {
    id: 'after',
    question: "What are you actually after?",
    answers: [
      {
        text: 'Something that outlasts this era of my life',
        traits: { builder_energy: 0.95, future_oriented: 0.9, ambitious: 0.85 },
      },
      {
        text: 'People who match my frequency',
        traits: { romantic: 0.85, emotionally_intense: 0.8, curious: 0.75 },
      },
      {
        text: "Stopping pretending certain things don't matter to me",
        traits: { emotionally_intense: 0.85, introspective: 0.8, guarded: 0.2 },
      },
      {
        text: 'Movement — direction matters less than velocity',
        traits: { ambitious: 0.9, future_oriented: 0.85, chaotic: 0.7 },
      },
      {
        text: 'All of it, simultaneously, always',
        traits: { chaotic: 0.8, ambitious: 0.85, builder_energy: 0.7, emotionally_intense: 0.8 },
      },
    ],
  },
];

// ── Observations — the real product ──────────────────────────────────
// These are selected based on highest-scoring hidden traits.
// They should feel accurate and slightly unsettling.

export var OBSERVATIONS = {
  builder_energy: [
    "You seem like someone whose projects outlast their phases.",
    "You probably disappear into things that matter to you.",
    "There's usually something in the background you're building toward.",
  ],
  emotionally_intense: [
    "You probably act less affected than you actually are.",
    "Certain things land harder on you than you let on.",
    "You remember specific moments longer than most people would.",
  ],
  chaotic: [
    "Your best decisions probably looked questionable at the time.",
    "You've turned 'one more hour' into something approaching a philosophy.",
  ],
  romantic: [
    "You romanticize specific types of moments — not everything, just certain things.",
    "You'd go further out of your way than you'd admit for the right conversation.",
    "You value chemistry you don't have to explain.",
  ],
  ambitious: [
    "Stagnation is harder on you than it should be.",
    "You're usually thinking a few versions ahead of whatever you're currently in.",
  ],
  playful: [
    "Humor is how you figure out if someone is actually worth knowing.",
    "You probably have a very specific thing that makes you laugh every time.",
  ],
  underground_scene: [
    "You've had important conversations in parking lots.",
    "You can tell almost immediately whether a room has the right energy.",
  ],
  introspective: [
    "You replay things. Not obsessively — just until they actually make sense.",
    "You've probably built an internal story around a few specific moments.",
    "You think about things longer than you let on.",
  ],
  aesthetic_driven: [
    "The atmosphere of a space affects your mood more than is convenient.",
    "Certain aesthetics do something to you that's hard to explain to most people.",
  ],
  guarded: [
    "People who know you well know a specifically different version of you.",
    "Access is earned with you. That's not coldness — it's just how it works.",
  ],
  tender: [
    "You notice small things about people. You don't always say anything.",
    "You care more about certain people than your general presentation would suggest.",
  ],
  curious: [
    "You have at least one obsession that most people don't fully understand.",
    "You find very different types of people interesting for completely different reasons.",
  ],
  empathetic: [
    "You read rooms faster than you let on.",
    "Other people's emotional states affect you more than is always convenient.",
  ],
  future_oriented: [
    "You're rarely fully settled in the present — there's always a parallel track.",
    "The current version of things is never quite the final version to you.",
  ],
};

// ── Signal strength — the only thing close to a score ─────────────────
// Shown once, quietly, at the bottom of results.

export function getSignalStrength(score) {
  if (score <= 25) return 'faint';
  if (score <= 42) return 'adjacent';
  if (score <= 58) return 'resonant';
  if (score <= 74) return 'strong';
  if (score <= 88) return 'dangerously familiar';
  return 'uncanny overlap';
}

// ── Composite pattern detection — influences observation selection ─────
// These are never shown directly. They push certain observations to the top.

var COMPOSITES = [
  {
    key: 'beautifully_difficult',
    boostTraits: ['romantic', 'guarded', 'emotionally_intense'],
    check: function(n) { return n('romantic') > 0.70 && n('guarded') > 0.60 && n('emotionally_intense') > 0.65; },
  },
  {
    key: 'identity_fused_to_purpose',
    boostTraits: ['builder_energy', 'ambitious', 'emotionally_intense'],
    check: function(n) { return n('builder_energy') > 0.80 && n('ambitious') > 0.75 && n('emotionally_intense') > 0.68; },
  },
  {
    key: 'avoidant_romantic',
    boostTraits: ['romantic', 'guarded', 'introspective'],
    check: function(n) { return n('romantic') > 0.72 && n('guarded') > 0.65 && n('introspective') > 0.65; },
  },
  {
    key: 'tender_chaos_agent',
    boostTraits: ['playful', 'tender', 'chaotic'],
    check: function(n) { return n('playful') > 0.75 && n('tender') > 0.62 && n('chaotic') > 0.52; },
  },
  {
    key: 'underground_intellectual',
    boostTraits: ['underground_scene', 'curious', 'introspective'],
    check: function(n) { return n('underground_scene') > 0.72 && n('curious') > 0.70 && n('introspective') > 0.62; },
  },
  {
    key: 'movement_as_survival',
    boostTraits: ['ambitious', 'chaotic', 'future_oriented'],
    check: function(n) { return n('ambitious') > 0.78 && n('chaotic') > 0.62 && n('future_oriented') > 0.72; },
  },
];

// ── Scoring ───────────────────────────────────────────────────────────

export function computeResonance(accumulated) {
  var traitKeys = Object.keys(ABEL_FREQUENCY);
  var maxAccum = Math.max.apply(null, traitKeys.map(function(t) { return accumulated[t] || 0; }).concat([0.001]));
  var dotProduct = 0;
  var normFactor = 0;
  traitKeys.forEach(function(t) {
    var u = (accumulated[t] || 0) / maxAccum;
    dotProduct += u * ABEL_FREQUENCY[t];
    normFactor += ABEL_FREQUENCY[t];
  });
  return Math.round((dotProduct / normFactor) * 100);
}

// ── Observation selection ─────────────────────────────────────────────
// Picks 4-5 observations. Composite detection boosts relevant traits.

export function pickObservations(accumulated, count) {
  var c = count || 4;
  var maxVal = Math.max.apply(null, Object.values(accumulated).concat([0.001]));
  var rawNorm = function(t) { return (accumulated[t] || 0) / maxVal; };

  // Check composites and boost their traits
  var boosted = Object.assign({}, accumulated);
  COMPOSITES.forEach(function(comp) {
    if (comp.check(rawNorm)) {
      comp.boostTraits.forEach(function(t) {
        boosted[t] = (boosted[t] || 0) * 1.2;
      });
    }
  });

  var boostedMax = Math.max.apply(null, Object.values(boosted).concat([0.001]));
  var norm = function(t) { return (boosted[t] || 0) / boostedMax; };

  var candidates = [];
  Object.keys(OBSERVATIONS).forEach(function(trait) {
    var score = norm(trait);
    if (score > 0.38) {
      // Pick one line per trait (the first one, or vary by score)
      var lines = OBSERVATIONS[trait];
      var idx = Math.floor(score * lines.length) % lines.length;
      candidates.push({ line: lines[idx], score: score, trait: trait });
    }
  });

  candidates.sort(function(a, b) { return b.score - a.score; });

  var seen = {};
  var result = [];
  for (var i = 0; i < candidates.length && result.length < c; i++) {
    var cand = candidates[i];
    if (!seen[cand.trait]) {
      result.push(cand.line);
      seen[cand.trait] = true;
    }
  }
  return result;
}
